import React, { useEffect, useRef } from 'react';
import SectionTitle from '../components/shared/SectionTitle';
import { Card, CardContent } from '../components/ui/card';
import { siteSettings } from '../data/mock';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';

const RendezVousPage = () => {
  const calInitialized = useRef(false);

  useEffect(() => {
    // Prevent double initialization
    if (calInitialized.current) return;
    calInitialized.current = true;

    // Load Cal.com embed script
    (function (C, A, L) {
      let p = function (a, ar) {
        a.q.push(ar);
      };
      let d = C.document;
      C.Cal =
        C.Cal ||
        function () {
          let cal = C.Cal;
          let ar = arguments;
          if (!cal.loaded) {
            cal.ns = {};
            cal.q = cal.q || [];
            d.head.appendChild(d.createElement("script")).src = A;
            cal.loaded = true;
          }
          if (ar[0] === L) {
            const api = function () {
              p(api, arguments);
            };
            const namespace = ar[1];
            api.q = api.q || [];
            if (typeof namespace === "string") {
              cal.ns[namespace] = cal.ns[namespace] || api;
              p(cal.ns[namespace], ar);
              p(cal, ["initNamespace", namespace]);
            } else p(cal, ar);
            return;
          }
          p(cal, ar);
        };
    })(window, "https://app.cal.eu/embed/embed.js", "init");

    // Initialize Cal
    window.Cal("init", "foulees-vertes", { origin: "https://app.cal.eu" });

    // Create inline embed
    window.Cal.ns["foulees-vertes"]("inline", {
      elementOrSelector: "#cal-booking",
      calLink: "foulees-vertes",
      layout: "month_view",
      config: {
        theme: "light"
      }
    });

    // Style customization - using only cssVarsPerTheme (styles is deprecated)
    window.Cal.ns["foulees-vertes"]("ui", {
      theme: "light",
      cssVarsPerTheme: {
        light: {
          "cal-bg": "transparent",
          "cal-bg-emphasis": "transparent", 
          "cal-bg-subtle": "transparent",
          "cal-bg-muted": "transparent",
          "cal-border": "#e5e7eb",
          "cal-border-emphasis": "#d1d5db",
          "cal-border-subtle": "#e5e7eb",
          "cal-border-default": "#e5e7eb",
          "cal-border-booker": "transparent",
          "cal-brand": "#047857",
          "cal-brand-emphasis": "#065f46",
        }
      },
      hideEventTypeDetails: false
    });
  }, []);

  const formatHours = (day) => {
    const hours = siteSettings.hours[day];
    if (!hours) return 'Fermé';
    return hours.display || `${hours.open} - ${hours.close}`;
  };

  const daysOfWeek = [
    { key: 'monday', label: 'Lundi' },
    { key: 'tuesday', label: 'Mardi' },
    { key: 'wednesday', label: 'Mercredi' },
    { key: 'thursday', label: 'Jeudi' },
    { key: 'friday', label: 'Vendredi' },
    { key: 'saturday', label: 'Samedi' },
    { key: 'sunday', label: 'Dimanche' },
  ];

  return (
    <div className="min-h-screen bg-white pt-32 pb-20">
      <style>{`
        #cal-booking {
          margin-top: -20px;
        }
      `}</style>
      
      <div className="max-w-7xl mx-auto px-4">
        <SectionTitle
          title="Prendre Rendez-vous"
          subtitle="Réservez votre analyse de foulée en ligne"
        />

        <div className="grid lg:grid-cols-3 gap-8 mt-12 items-start">
          {/* Cal.com Embed - Takes 2 columns */}
          <div className="lg:col-span-2">
            <div 
              id="cal-booking" 
              style={{ width: '100%', height: '100%', minHeight: '600px' }}
            />
          </div>

          {/* Contact Info - 1 column */}
          <div className="space-y-6">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Contact direct</h3>
                <ul className="space-y-4">
                  <li>
                    <a
                      href={`tel:${siteSettings.contact.phone}`}
                      className="flex items-start gap-3 text-gray-700 hover:text-emerald-700 transition-colors"
                    >
                      <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Phone className="w-4 h-4 text-emerald-700" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">Téléphone</p>
                        <p className="text-gray-600 text-sm">{siteSettings.contact.phoneDisplay || siteSettings.contact.phone}</p>
                      </div>
                    </a>
                  </li>
                  <li>
                    <a
                      href={`mailto:${siteSettings.contact.email}`}
                      className="flex items-start gap-3 text-gray-700 hover:text-emerald-700 transition-colors"
                    >
                      <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Mail className="w-4 h-4 text-emerald-700" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">Email</p>
                        <p className="text-gray-600 text-sm">{siteSettings.contact.email}</p>
                      </div>
                    </a>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-4 h-4 text-emerald-700" />
                    </div>
                    <div>
                      <p className="font-medium text-sm text-gray-900">Adresse</p>
                      <p className="text-gray-600 text-sm">{siteSettings.contact.address}</p>
                    </div>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-emerald-600" />
                  Horaires
                </h3>
                <ul className="space-y-1.5 text-sm">
                  {daysOfWeek.map((day) => (
                    <li key={day.key} className="flex justify-between py-1 border-b border-gray-100 last:border-0">
                      <span className="font-medium text-gray-700">{day.label}</span>
                      <span className={!siteSettings.hours[day.key] ? 'text-gray-400' : 'text-gray-600'}>
                        {formatHours(day.key)}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-gray-500 mt-3">* Ouvertures ponctuelles</p>
              </CardContent>
            </Card>

            {/* Info box */}
            <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-100">
              <h4 className="font-semibold text-emerald-800 mb-2">Bon à savoir</h4>
              <p className="text-sm text-emerald-700">
                Pensez à apporter vos anciennes chaussures de running lors de votre rendez-vous pour un diagnostic plus précis.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RendezVousPage;
